<template>
  <div
    class="wt-toolbox-bgmusic"
    v-tooltip="$_tooltip_onToggle"
    ref="tooltipBody"
    @mouseenter="$_toggleHover"
    @mouseleave="$_toggleHover"
  >
    <i class="wticon wticon-music-alt wt2-color-normal" />
    <div class="-bg-label wt2-link" v-text="label" @click="$_edit" />
    <wt-toggle v-model="hasMusic" @toggle="$_onToggleChange" />
  </div>
</template>

<script>
import app from 'app';
import BGMusicSelection from './BGMusicSelection.vue';
import _ from 'lodash-extended';
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';

export default Vue.extend({
  name: 'wt-toolbox-bgmusic',

  data() {
    return {
      hasMusic: !!this.music,
      isHovered: false,
    };
  },

  computed: {
    ...mapState({
      music: (state) => state.story.storyBackgroundMusic,
    }),
    ...mapState('timemachine', {
      showBeats: (state) => state.USE.showBeats,
    }),
    ...mapGetters(['hasAudioOnTimeline']),

    isEmpty() {
      return _.isEmpty(this.music);
    },

    label() {
      return app.culture.get('toolbox.name');
    },

    musicTitle() {
      return this.isEmpty
        ? ''
        : this.music.metadata.title || this.music.metadata.caption || '';
    },

    $_tooltip_onToggle() {
      let title;
      let description;

      if (this.hasMusic) {
        title = app.culture.get('bgMusic.tooltip.on.title');
        description = app.culture.get('bgMusic.tooltip.on.fnDescription')(
          this.musicTitle
        );
      } else {
        title = app.culture.get('bgMusic.tooltip.off.title');
        description = app.culture.get('bgMusic.tooltip.off.description');
      }

      return {
        animation: false,
        wrapperClass: 'large -bgmusic',
        trigger: 'hover',
        placement: 'top',
        viewport: { selector: 'body, .wt-story-toolbox', padding: 10 },
        delay: { show: 300, hide: 0 },
        title: `
          <div class="bgmusic-tooltip">
            <div class="-title">${title}</div>
            <div class="-description">${description}</div>
          </div>
        `.normalizeSpace(),
        html: true,
      };
    },
  },

  watch: {
    music: {
      immediate: true,
      handler() {
        this.hasMusic = !this.isEmpty;
      },
    },

    hasMusic() {
      this.$nextTick(() => {
        if (!this._isDestroyed && this.isHovered) {
          $(this.$refs.tooltipBody).tooltip(this.hasMusic ? 'hide' : 'show');
        }
      });
    },
  },

  methods: {
    ...mapActions('timemachine', ['TOGGLE_BEATS']),

    $_toggleHover(e) {
      this.isHovered = e.type === 'mouseenter';
    },

    $_onToggleChange() {
      if (this.isEmpty) {
        this.$_edit();
      } else {
        this.$_delete();
        this.hasMusic = false;
      }
    },

    $_edit: _.debounce(
      function () {
        const story = app.storyController.story;
        const isVideoTemplate = !!story.get('template');

        this.mixinShowViewInModal({
          type: 'big',
          component: BGMusicSelection,
          componentPropsData: {
            initiallySelected: this.music,
            options: {
              userId: this.$store.getters['user/id'],
              programmerId: this.$store.state.story.programmerId,
              isVideoTemplate,
              storyHasNarration: this.hasAudioOnTimeline,
            },
            onToggleSelect: ({ selected }) => {
              this.$store.dispatch('SET_STORY_BACKGROUND_MUSIC', selected);
              this.hasMusic = !!selected;
            },
          },
          closeOnEscape: true,
          closeOnClickOut: false,
          showCloseButton: true,
        });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),

    $_delete: _.debounce(
      function () {
        this.$store.dispatch('SET_STORY_BACKGROUND_MUSIC', null);
        this.showBeats && this.TOGGLE_BEATS();

        app.analytics('analytics:timeline:removedBGMusic', {
          'Timeline Type': this.hasAudioOnTimeline ? 'Text' : 'Time',
          'Track Name': this.label,
        });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),
  },
});
</script>

<style lang="scss">
.tooltip.wt-preset-tooltip.large.-bgmusic {
  pointer-events: none;
  margin-top: 10px; // move it down

  .tooltip-inner {
    max-width: 300px;
  }

  .-description {
    word-break: break-all;
  }
}
</style>

<style lang="scss" scoped>
.wt-toolbox-bgmusic {
  display: flex;
  justify-content: center;
  align-items: center;
  @include user-select(none);
  max-width: 173px;

  &:hover .-delete {
    visibility: visible;
  }

  .wticon {
    font-weight: 500;
    font-size: 16px;
  }

  .-bg-label {
    cursor: pointer;
    margin: 0 0 0 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .-delete {
    display: flex;
    visibility: collapse;
    height: 22px;
    width: 25px;
    justify-content: center;
    align-items: center;
    margin: 0 0 0 2px;
    padding-top: 2px;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      color: $wt-violet-dark2;
    }

    .wticon {
      font-weight: 700;
      font-size: 10px;
    }
  }

  .wt2-toggle-component {
    height: 20px;
    margin-left: 10px;
  }
}
</style>
